import React from "react";

import { FallbackRender } from "@sentry/react/types/errorboundary";
import { defineMessages } from "react-intl";

import { Button } from "@mapmycustomers/ui";

import { ReactComponent as MiniLogo } from "@app/assets/logo-mini.svg";
import i18nService from "@app/config/I18nService";
import Path from "@app/enum/Path";

import styles from "./ErrorFallback.module.scss";

const messages = defineMessages({
  description: {
    id: "errorFallback.description",
    defaultMessage: "We've encountered an unexpected error while processing your request.",
    description: "Description on the Error page",
  },
  error: {
    id: "errorFallback.error",
    defaultMessage: "Error",
    description: "Error on the Error page",
  },
  footer: {
    id: "errorFallback.footer",
    defaultMessage:
      "If the problem persists, please reach out to our support team. We're here to help you get back on track!",
    description: "Footer of the Error page",
  },
  goBack: {
    id: "errorFallback.actions.goBack",
    defaultMessage: "Go Back",
    description: "Go back button on the Error page",
  },
  goToDashboard: {
    id: "errorFallback.actions.goToDashboard",
    defaultMessage: "Go to Dashboard",
    description: "Go to Dashboard button on the Error page",
  },
  subtitle: {
    id: "errorFallback.subtitle",
    defaultMessage: "Something went wrong",
    description: "Subtitle of an Error page",
  },
  title: {
    id: "errorFallback.title",
    defaultMessage: "Oops!",
    description: "Title of an Error page",
  },
});

// We cannot use any hooks in this method, hence we use i18nService instead of
// useIntl.
// eslint-disable-next-line react/prop-types
const ErrorFallback: FallbackRender = ({ resetError }) => {
  return (
    <div className={styles.container}>
      <MiniLogo className={styles.logo} />
      <div className={styles.error}>{i18nService.formatMessage(messages.error, "Error")}</div>
      <div className={styles.title}>{i18nService.formatMessage(messages.title, "Oops!")}</div>
      <div className={styles.subtitle}>
        {i18nService.formatMessage(messages.subtitle, "Something went wrong!")}
      </div>
      <div className={styles.description}>
        {i18nService.formatMessage(
          messages.description,
          "We've encountered an unexpected error while processing your request."
        )}
      </div>
      <div className={styles.actions}>
        <Button
          onClick={() => {
            window.location.href = Path.DASHBOARD;
          }}
          size="large"
          type="primary"
        >
          {i18nService.formatMessage(messages.goToDashboard, "Go to Dashboard")}
        </Button>
        <Button onClick={resetError} size="large">
          {i18nService.formatMessage(messages.goBack, "Go Back")}
        </Button>
      </div>
      <div className={styles.footer}>
        {i18nService.formatMessage(
          messages.footer,
          "If the problem persists, please reach out to our support team. We're here to help you get back on track!"
        )}
      </div>
    </div>
  );
};

export default ErrorFallback;
